import { ref } from 'vue'
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

/**
 * 주간 랭킹 Composable
 *
 * 랭킹 종류:
 * 1. 현장 조회수 랭킹 - 이번 주 가장 많이 본 현장 TOP 10
 * 2. 상담 신청 랭킹 - 이번 주 상담 신청이 가장 많은 현장 TOP 10
 * 3. 커뮤니티 인기글 - 이번 주 좋아요/댓글 많은 게시글 TOP 10
 * 4. 인기 상담사 랭킹 - 이번 주 상담 신청 가장 많이 받은 상담사 TOP 10
 */
export function useWeeklyRanking() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 이번 주 시작일 (월요일 00:00:00) 가져오기
   */
  const getWeekStartDate = () => {
    const now = new Date()
    const day = now.getDay()
    const diff = now.getDate() - day + (day === 0 ? -6 : 1) // 월요일로 조정
    const monday = new Date(now.setDate(diff))
    monday.setHours(0, 0, 0, 0)
    return monday
  }

  /**
   * 현장 조회수 랭킹
   * properties 컬렉션의 weeklyViews 필드 기준
   */
  const getPropertyViewRanking = async (limitCount = 10) => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'properties'),
        orderBy('weeklyViews', 'desc'),
        limit(limitCount)
      )
      const snapshot = await getDocs(q)

      return snapshot.docs
        .map((doc, index) => ({
          rank: index + 1,
          id: doc.id,
          ...doc.data()
        }))
        .filter(p => (p.weeklyViews || 0) > 0)
    } catch (err) {
      console.error('현장 조회수 랭킹 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 상담 신청 랭킹 (현장별)
   * 이번 주 consultationRequests에서 propertyId별 집계
   */
  const getConsultationRanking = async (limitCount = 10) => {
    loading.value = true
    error.value = null

    try {
      const weekStart = getWeekStartDate()

      // 이번 주 상담 신청 조회
      const q = query(
        collection(db, 'consultationRequests'),
        where('createdAt', '>=', weekStart.toISOString())
      )
      const snapshot = await getDocs(q)

      // propertyId별 집계
      const propertyCount = {}
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        const propertyId = data.propertyId
        if (propertyId) {
          if (!propertyCount[propertyId]) {
            propertyCount[propertyId] = {
              propertyId,
              propertyName: data.propertyName || '알 수 없음',
              count: 0
            }
          }
          propertyCount[propertyId].count++
        }
      })

      // 정렬 및 순위 부여
      return Object.values(propertyCount)
        .sort((a, b) => b.count - a.count)
        .slice(0, limitCount)
        .map((item, index) => ({
          rank: index + 1,
          ...item
        }))
    } catch (err) {
      console.error('상담 신청 랭킹 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 커뮤니티 인기글 랭킹
   * 이번 주 생성된 게시글 중 likes + commentCount 합산 기준
   * 이번 주 글이 없어도 최소 1개는 반환 (최신글)
   */
  const getCommunityHotPosts = async (limitCount = 10, minCount = 1) => {
    loading.value = true
    error.value = null

    try {
      const weekStart = getWeekStartDate()

      // 이번 주 게시글 조회 (isBlinded 필드가 없는 문서도 포함)
      const q = query(
        collection(db, 'communityPosts'),
        where('createdAt', '>=', weekStart.toISOString())
      )
      const snapshot = await getDocs(q)

      // 인기도 점수 계산 (좋아요 + 댓글 수), 블라인드 제외
      let posts = snapshot.docs
        .map(doc => {
          const data = doc.data()
          const score = (data.likes || 0) + (data.commentCount || 0) * 2 // 댓글에 가중치
          return {
            id: doc.id,
            ...data,
            score
          }
        })
        .filter(post => !post.isBlinded) // 블라인드 된 게시글 제외

      // 이번 주 글이 minCount보다 적으면 최신글로 채우기
      if (posts.length < minCount) {
        const fallbackQ = query(
          collection(db, 'communityPosts'),
          orderBy('createdAt', 'desc'),
          limit(minCount * 2) // 여유있게 가져와서 필터링
        )
        const fallbackSnapshot = await getDocs(fallbackQ)
        posts = fallbackSnapshot.docs
          .map(doc => {
            const data = doc.data()
            const score = (data.likes || 0) + (data.commentCount || 0) * 2
            return {
              id: doc.id,
              ...data,
              score
            }
          })
          .filter(post => !post.isBlinded) // 블라인드 된 게시글 제외
      }

      // 정렬 및 순위 부여
      return posts
        .sort((a, b) => b.score - a.score)
        .slice(0, limitCount)
        .map((post, index) => ({
          rank: index + 1,
          ...post
        }))
    } catch (err) {
      console.error('커뮤니티 인기글 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 인기 상담사 랭킹
   * 이번 주 consultationRequests에서 partnerId별 집계
   */
  const getConsultantRanking = async (limitCount = 10) => {
    loading.value = true
    error.value = null

    try {
      const weekStart = getWeekStartDate()

      // 이번 주 상담 신청 조회
      const q = query(
        collection(db, 'consultationRequests'),
        where('createdAt', '>=', weekStart.toISOString())
      )
      const snapshot = await getDocs(q)

      // partnerId별 집계
      const partnerCount = {}
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        const partnerId = data.partnerId
        if (partnerId) {
          if (!partnerCount[partnerId]) {
            partnerCount[partnerId] = {
              partnerId,
              partnerDisplayName: data.partnerDisplayName || '알 수 없음',
              propertyName: data.propertyName || '',
              count: 0
            }
          }
          partnerCount[partnerId].count++
        }
      })

      // 정렬 및 순위 부여
      return Object.values(partnerCount)
        .sort((a, b) => b.count - a.count)
        .slice(0, limitCount)
        .map((item, index) => ({
          rank: index + 1,
          ...item
        }))
    } catch (err) {
      console.error('인기 상담사 랭킹 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 모든 랭킹 한번에 가져오기
   */
  const getAllRankings = async () => {
    loading.value = true

    try {
      const [propertyViews, consultations, hotPosts, consultants] = await Promise.all([
        getPropertyViewRanking(10),
        getConsultationRanking(10),
        getCommunityHotPosts(10),
        getConsultantRanking(10)
      ])

      return {
        propertyViews,
        consultations,
        hotPosts,
        consultants
      }
    } catch (err) {
      console.error('전체 랭킹 조회 실패:', err)
      error.value = err.message
      return {
        propertyViews: [],
        consultations: [],
        hotPosts: [],
        consultants: []
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * 현장 조회수 증가 (PropertyDetailView에서 호출)
   */
  const incrementPropertyView = async (propertyId) => {
    try {
      const { doc, updateDoc, increment } = await import('firebase/firestore')
      const propertyRef = doc(db, 'properties', propertyId)
      await updateDoc(propertyRef, {
        views: increment(1),
        weeklyViews: increment(1)
      })
    } catch (err) {
      console.error('조회수 증가 실패:', err)
    }
  }

  return {
    loading,
    error,
    getWeekStartDate,
    getPropertyViewRanking,
    getConsultationRanking,
    getCommunityHotPosts,
    getConsultantRanking,
    getAllRankings,
    incrementPropertyView
  }
}
